import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * localStorage-backed column order with drift-tolerant reconciliation.
 *
 * - Reads on mount; falls back to defaults when empty / invalid.
 * - Reconciles stored order against the current defaults: drops keys no
 *   longer in defaults, appends new keys at the end. Lets us add/remove
 *   columns later without breaking saved orders.
 * - Persists on every mutation. `reset()` clears the entry and restores
 *   defaults.
 */
export function useColumnOrder<K extends string>(
  storageKey: string | null,
  defaultOrder: readonly K[],
): {
  order: K[];
  moveColumn: (from: K, to: K) => void;
  reset: () => void;
  isDefault: boolean;
} {
  const reconcile = useCallback((stored: unknown): K[] => {
    if (!Array.isArray(stored)) return [...defaultOrder];
    const known = new Set<K>(defaultOrder);
    const seen = new Set<K>();
    const out: K[] = [];
    for (const k of stored) {
      if (typeof k === 'string' && known.has(k as K) && !seen.has(k as K)) {
        out.push(k as K);
        seen.add(k as K);
      }
    }
    for (const k of defaultOrder) {
      if (!seen.has(k)) out.push(k);
    }
    return out;
  }, [defaultOrder]);

  const read = useCallback((): K[] => {
    if (!storageKey) return [...defaultOrder];
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return [...defaultOrder];
      return reconcile(JSON.parse(raw));
    } catch {
      return [...defaultOrder];
    }
  }, [storageKey, defaultOrder, reconcile]);

  const [order, setOrder] = useState<K[]>(read);

  // Re-read when storage key changes (e.g. user logs in / switches account).
  const lastKeyRef = useRef(storageKey);
  useEffect(() => {
    if (lastKeyRef.current !== storageKey) {
      lastKeyRef.current = storageKey;
      setOrder(read());
    }
  }, [storageKey, read]);

  const persist = useCallback((next: K[]) => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Quota / private mode — fail silently; in-memory order still updates.
    }
  }, [storageKey]);

  const moveColumn = useCallback((from: K, to: K) => {
    if (from === to) return;
    setOrder((prev) => {
      const fromIdx = prev.indexOf(from);
      const toIdx = prev.indexOf(to);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = prev.slice();
      next.splice(fromIdx, 1);
      const insertAt = next.indexOf(to);
      next.splice(insertAt, 0, from);
      persist(next);
      return next;
    });
  }, [persist]);

  const reset = useCallback(() => {
    if (storageKey) {
      try { window.localStorage.removeItem(storageKey); } catch { /* noop */ }
    }
    setOrder([...defaultOrder]);
  }, [storageKey, defaultOrder]);

  const isDefault = order.length === defaultOrder.length
    && order.every((k, i) => k === defaultOrder[i]);

  return { order, moveColumn, reset, isDefault };
}
